import {
  // This is the one module allowed to render MUI's IconButton directly.
  // eslint-disable-next-line no-restricted-imports
  IconButton as MuiIconButton,
  styled,
  type IconButtonProps as MuiIconButtonProps,
} from '@mui/material';
import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { Tooltip, type TooltipProps } from '~/components/Tooltip';

export interface IconButtonLabelProps {
  /**
   * The button's accessible name and its tooltip text. One string feeds both, so what a
   * screen reader announces always matches what a sighted user sees on hover.
   */
  label: string;
  /** Extra tooltip text shown under the label, such as why the button is disabled. */
  description?: ReactNode;
  tooltipPlacement?: TooltipProps['placement'];
}

export interface IconButtonTooltipProps extends IconButtonLabelProps {
  children: ReactElement;
}

const tooltipTitle = (label: string, description: ReactNode) =>
  description == null ? (
    label
  ) : (
    <>
      {label}
      <br />
      {description}
    </>
  );

/**
 * The tooltip half of an icon-only button. The child must set `aria-label={label}` itself;
 * this only supplies the matching tooltip.
 */
export const IconButtonTooltip = ({
  children,
  description,
  label,
  tooltipPlacement,
}: IconButtonTooltipProps) => {
  // A disabled button fires no pointer events, so the tooltip listens on a wrapper. The wrapper
  // is always rendered so toggling `disabled` never remounts the button or drops its focus.
  // The default `describeChild` keeps MUI from labelling that span, which is not a control.
  return (
    <Tooltip placement={tooltipPlacement} title={tooltipTitle(label, description)}>
      <span>{children}</span>
    </Tooltip>
  );
};

// A grid action is disabled through aria-disabled (see `IconButton`), so MUI's disabled styling
// is applied here, without the `pointer-events: none` that would keep the tooltip from opening.
const MenuItemIconButton = styled(MuiIconButton)(({ theme }) => ({
  '&[aria-disabled="true"]': {
    color: theme.palette.action.disabled,
    cursor: 'default',
    '&:hover': { backgroundColor: 'transparent' },
  },
}));

export type IconButtonProps = Omit<MuiIconButtonProps, 'aria-label' | 'ref' | 'title'> &
  IconButtonLabelProps &
  // With an href, the theme's ButtonBase LinkComponent renders it as a router link.
  Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'rel' | 'target'>;

/**
 * An icon-only button. `label` is required and becomes both the accessible name and the
 * tooltip, so an icon button can never announce as just "button".
 */
export const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, IconButtonProps>(
  function IconButton({ description, label, tooltipPlacement, ...props }, ref) {
    // MUI types the ref for its default button; with an href the element is an anchor.
    const buttonRef = ref as Ref<HTMLButtonElement>;

    // A data grid actions cell (role=menu, see `withMenuItemActions`) moves focus between its
    // direct DOM children, so an action in one holds its tooltip itself rather than sitting in
    // the wrapper span, and stays that same bare element whether or not it is disabled. As with
    // a `TooltipMenuItem`, a disabled action is aria-disabled: still a live element that takes
    // the pointer for its tooltip, but one that drops its clicks. The cell reads `disabled` off
    // the element it clones, so it never moves keyboard focus onto one. With only a label the
    // tooltip is the button's name, so it must not also describe it.
    if (props.role === 'menuitem') {
      const { disabled, disableRipple, onClick, ...rest } = props;
      return (
        <Tooltip
          describeChild={description != null}
          placement={tooltipPlacement}
          title={tooltipTitle(label, description)}
        >
          <MenuItemIconButton
            {...rest}
            aria-disabled={disabled || undefined}
            aria-label={label}
            disableRipple={disabled || disableRipple}
            onClick={disabled ? undefined : onClick}
            ref={buttonRef}
          />
        </Tooltip>
      );
    }

    return (
      <IconButtonTooltip
        description={description}
        label={label}
        tooltipPlacement={tooltipPlacement}
      >
        <MuiIconButton {...props} aria-label={label} ref={buttonRef} />
      </IconButtonTooltip>
    );
  },
);
